// packages/core/src/learning/school-modules/fi-math.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "FI math". surface: 'school', homeSchool: 'fi-math'.
// module id 'school-fi-math' matches the exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #17897a) — flag for the presentation-tuning pass.
// tier/pillar: freedom-path lens → tier 'freedom-path', pillar 'freedom-math'.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_FI_MATH_MODULE: LearningModule = {
	id: 'school-fi-math',
	title: 'FI math',
	description:
		'The lens that turns vague freedom into testable numbers — savings rate, runway, and timelines — without pretending the future is guaranteed.',
	emoji: '🧮', // PLACEHOLDER — tune in presentation pass
	accentColor: '#17897a', // from SchoolMeta color — tune in presentation pass
	tier: 'freedom-path',
	pillar: 'freedom-math',
	order: 0,
	surface: 'school',
	homeSchool: 'fi-math',
	lessons: [
		{
			id: 'school-fi-math-01',
			title: 'Freedom needs a number',
			description:
				'This lesson teaches how FI math turns vague freedom into something the user can test.',
			pillar: 'freedom-math',
			schoolsOfThought: ['fi-math'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['freedom-target', 'assumptions', 'testable-plan'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'freedom target',
				'visible assumptions',
				'testable plan',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Freedom sounds good until it stays vague. FI math pushes the dream into the open by asking what the life actually costs.',
				},
				{
					type: 'paragraph',
					text: 'This school believes financial independence becomes more useful when it becomes testable. The point is not to worship one number. The point is to see what assumptions sit underneath the goal.',
				},
				{
					type: 'bullet_list',
					items: [
						'Spending sets the target',
						'Assumptions shape the timeline',
						'The number can change',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating the FI number like destiny instead of a planning estimate.',
				},
				{
					type: 'example',
					text: 'Someone who spends $3,000 per month is solving a different freedom problem than someone who spends $6,000 per month. The math does not judge either person. It shows the tradeoff.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is false certainty. A clean number can make the future feel more predictable than it really is.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to building a freedom plan. You use the number to think better, then update it when life, income, spending, or assumptions change.',
				},
				{
					type: 'key_takeaway',
					text: 'FI math makes freedom visible, but it does not make the future guaranteed.',
				},
			],
			audioScript:
				'FI math starts with a simple challenge: freedom needs a number. Not because one number can explain your whole life, and not because a spreadsheet knows your future. It needs a number because vague goals are hard to test.\n\nA person can say, “I want financial freedom,” but that can mean many different things. It might mean quitting work forever. It might mean being able to leave a bad job. It might mean working part time. It might mean being able to help family without panic. It might mean taking a career risk. Until you attach the goal to spending, assets, runway, and assumptions, the dream stays blurry.\n\nFI math is the lens that turns blur into something you can inspect. It asks: what does your life cost? What would it cost if you made different choices? How much would need to be invested to support that life? What assumptions are you making about returns, inflation, taxes, flexibility, and time? What happens if those assumptions change?\n\nThis is why spending matters so much. Spending does not only affect how much cash leaves your account today. It affects the size of the freedom target. A person spending $3,000 per month is solving a different problem than a person spending $6,000 per month. Neither person is morally better. The math is not there to shame anyone. It is there to reveal the tradeoff.\n\nThat is one of the strongest parts of this school. It makes lifestyle visible. It shows that a higher spending life may require more assets, more income, more time, or more risk. A lower spending life may require less, but it may also involve tradeoffs the person does not want. FI math does not choose for you. It shows the price of different versions of freedom.\n\nBut the blind spot is serious. Once people have a number, they can start treating it like truth. They say, “My FI number is this,” as if the future signed a contract. But the number is only as good as the assumptions behind it. Markets can disappoint. Inflation can rise. Rent can change. Health can change. Family needs can change. Your desired life can change. Your own values can change.\n\nThat does not make the number useless. It means the number should stay alive. A good FI number is a planning estimate, not a prophecy. It gives you a place to start asking better questions.\n\nInside Nisk, this lens should feel grounding. Build a freedom plan. Look at the target. Check your savings rate. Look at runway. Then ask what the number depends on. The goal is not to become a spreadsheet person. The goal is to stop letting your future stay fuzzy.\n\nThe takeaway is simple. FI math makes freedom visible, but it does not make the future guaranteed. Use the number to think better, not to pretend life cannot change.',
			flashcards: [
				{
					id: 'school-fi-math-01-fc-1',
					role: 'main_idea',
					front: 'Why does freedom need a number?',
					back: 'A number makes the goal testable instead of vague.',
				},
				{
					id: 'school-fi-math-01-fc-2',
					role: 'watch_out',
					front: 'What is the FI number trap?',
					back: 'Treating a planning estimate like a guaranteed future.',
				},
				{
					id: 'school-fi-math-01-fc-3',
					role: 'use_it',
					front: 'How should the number be used?',
					back: 'Use it to inspect spending, assumptions, and tradeoffs.',
				},
			],
			applyAction: {
				type: 'buildFreedomPlan',
				label: 'Build your freedom plan',
			},
			revision: 1,
		},
		{
			id: 'school-fi-math-02',
			title: 'Savings rate controls both sides',
			description:
				'This lesson teaches why FI math sees savings rate as powerful because it affects both investing fuel and lifestyle cost.',
			pillar: 'freedom-math',
			schoolsOfThought: ['fi-math'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['savings-rate', 'lifestyle-cost', 'time-to-freedom'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'savings rate leverage',
				'lifestyle cost',
				'time to freedom',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Income gets attention because it feels like the main lever. FI math watches savings rate because it controls two sides of the freedom equation.',
				},
				{
					type: 'paragraph',
					text: 'This school believes savings rate matters because it shows how much income becomes future options and how expensive your lifestyle is to maintain.',
				},
				{
					type: 'bullet_list',
					items: [
						'More saved creates fuel',
						'Less spent lowers the target',
						'Time changes on both sides',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is chasing higher income while letting every raise become normal spending.',
				},
				{
					type: 'example',
					text: 'If two people earn the same amount, the person saving 30 percent is building future options faster than the person saving 5 percent. They are also proving they can live on less.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that a high savings rate can become unhealthy if it turns every present choice into guilt.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to checking how savings rate changes timelines. The point is not maximum restriction. The point is seeing what the rate changes.',
				},
				{
					type: 'key_takeaway',
					text: 'Savings rate matters because it fuels the future and defines the lifestyle the future must support.',
				},
			],
			audioScript:
				'FI math pays a lot of attention to savings rate. At first, that can sound like basic budgeting. It is bigger than that. Savings rate is powerful because it controls both sides of the freedom equation.\n\nOne side is fuel. The more of your income you save and invest, the more money gets sent toward future options. That part is easy to understand. If someone saves 5 percent of income, the future gets a small amount of fuel. If someone saves 25 percent or 40 percent, the future gets much more fuel. The invested base can grow faster, and compounding has more to work with.\n\nThe other side is lifestyle cost. This is the part people often miss. If you save a high percentage of income, that also means you are living on a lower percentage of income. Your future life may need less money to support, assuming that lifestyle is real and sustainable. So savings rate does two jobs at once. It increases the amount going toward freedom, and it can lower the size of the freedom target.\n\nThat is why FI math often treats savings rate as a stronger signal than income alone. A high income with no savings may create comfort, but it may not create freedom. A moderate income with a strong savings rate may create more options than people expect. This does not mean income is unimportant. Income can be a huge lever. But income only becomes freedom if some of it escapes the spending machine.\n\nA realistic example: two people earn the same amount. One saves 5 percent. The other saves 30 percent. The second person is not just investing more. They are also showing that their required lifestyle costs less. That can shorten the path from both directions.\n\nBut this lens has a blind spot. Savings rate can become a purity test. People can start treating every dollar spent as failure. They can push the rate so high that life becomes joyless, relationships feel strained, or basic health gets ignored. That is not better judgment. That is the spreadsheet taking over the person.\n\nFI math should reveal tradeoffs, not create shame. A higher savings rate may create a faster path, but it may also cost comfort, experiences, time, convenience, or social life. A lower savings rate may create more room now, but it may extend the timeline. The point is not to declare one answer correct. The point is to see what each answer does.\n\nInside Nisk, this is exactly why the savings rate action matters. You can test what happens when the rate changes. What if you save more? What if you earn more and keep spending steady? What if spending rises with income? What if a small monthly leak becomes a multi year delay? The math turns vague tradeoffs into visible ones.\n\nThe best version of this lens is not “save everything.” It is “know what your savings rate is buying you.” Sometimes it buys speed. Sometimes it buys breathing room. Sometimes it reveals that income is high but freedom is not moving. Sometimes it shows that a modest improvement could matter more than you thought.\n\nThe takeaway is simple. Savings rate matters because it fuels the future and defines the lifestyle the future must support. It is not the only number, but it is one of the clearest ones.',
			flashcards: [
				{
					id: 'school-fi-math-02-fc-1',
					role: 'main_idea',
					front: 'Why does savings rate matter?',
					back: 'It creates investing fuel and shows lifestyle cost.',
				},
				{
					id: 'school-fi-math-02-fc-2',
					role: 'watch_out',
					front: 'What is the raise trap?',
					back: 'Letting every income increase become normal spending.',
				},
				{
					id: 'school-fi-math-02-fc-3',
					role: 'use_it',
					front: 'What should you test?',
					back: 'Test how different savings rates change the freedom timeline.',
				},
			],
			applyAction: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
			revision: 1,
		},
		{
			id: 'school-fi-math-03',
			title: 'The math is not the life',
			description:
				'This lesson teaches where FI math breaks when projections replace judgment, flexibility, and real human needs.',
			pillar: 'freedom-math',
			schoolsOfThought: ['fi-math'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['projection-humility', 'model-limits', 'flexibility'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'projection humility',
				'life variable risk',
				'model limits',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'FI math is useful because it makes the hidden visible. It breaks when the model becomes more trusted than reality.',
				},
				{
					type: 'paragraph',
					text: 'This school believes numbers can improve judgment, but the numbers are built from assumptions. Real life can change faster than the spreadsheet.',
				},
				{
					type: 'bullet_list',
					items: [
						'Markets can change',
						'Spending can change',
						'You can change',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is optimizing the projection while ignoring the human life behind it.',
				},
				{
					type: 'example',
					text: 'A plan may look perfect if expenses stay flat, returns behave, health stays stable, and income keeps growing. But one family need, job change, move, or health issue can shift the whole picture.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is control illusion. FI math can make uncertainty feel smaller than it is.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens should end with regular check ins. Use the math to stay honest, then keep enough flexibility for life to be life.',
				},
				{
					type: 'key_takeaway',
					text: 'FI math should guide the life. It should not replace the life.',
				},
			],
			audioScript:
				'FI math is powerful because it makes hidden tradeoffs visible. It shows how spending changes the target. It shows how savings rate changes time. It shows how runway changes choices. It shows how assumptions shape the plan. That is useful. But FI math breaks when the model becomes more trusted than real life.\n\nA spreadsheet can feel clean. You type in income, expenses, savings rate, investment assumptions, and a timeline. The numbers respond. The chart moves. The future starts to look orderly. That can be motivating. It can also be misleading.\n\nThe future is not a spreadsheet. Markets can change. Inflation can change. Rent can change. Health can change. A parent may need help. A child may arrive. A relationship may change. A job may disappear. A business may fail. Your values may shift. The life you thought you wanted at 28 may not be the life you want at 38.\n\nFI math is not wrong because life changes. It becomes wrong when people forget that life can change.\n\nThis is the control illusion. A detailed projection can make uncertainty feel smaller than it really is. The more precise the model looks, the more tempting it is to believe it. But precision is not the same as truth. A number with two decimal places can still be based on a fragile assumption.\n\nA realistic example: someone builds a plan that works beautifully if expenses stay flat, income rises steadily, and markets deliver strong long term returns. The timeline looks clear. Then rent jumps, income stalls, a family obligation appears, or the person realizes they want a different lifestyle. The math did not fail because math is useless. It failed because the plan treated assumptions like guarantees.\n\nThis is why FI math needs humility. The numbers should guide the life, not replace the life. The point is not to optimize yourself into a miserable plan. The point is to understand tradeoffs so you can choose with more clarity.\n\nThere is also a human side. Some people use FI math to punish themselves. They turn every purchase into a delay. They turn every friend event into a spreadsheet problem. They chase the earliest possible date while ignoring health, joy, love, and meaning. That is not freedom. That is another form of control.\n\nInside Nisk, the better use of this lens is regular check ins. Look at the numbers, then ask what changed. Did spending move? Did income move? Did your goal move? Did your runway improve? Did your assumptions become too optimistic? Did your life start asking for a different plan?\n\nThat is mature FI math. It does not worship the model. It uses the model as a conversation with reality.\n\nThe takeaway is simple. FI math should guide the life. It should not replace the life. The best plan is not the one that looks perfect once. It is the one that can keep adapting without lying to you.',
			flashcards: [
				{
					id: 'school-fi-math-03-fc-1',
					role: 'main_idea',
					front: 'Where does FI math break?',
					back: 'When projections replace judgment, flexibility, and real life.',
				},
				{
					id: 'school-fi-math-03-fc-2',
					role: 'watch_out',
					front: 'What is control illusion?',
					back: 'A clean model making the uncertain future feel guaranteed.',
				},
				{
					id: 'school-fi-math-03-fc-3',
					role: 'use_it',
					front: 'How do you keep it honest?',
					back: 'Check assumptions regularly and update the plan when life changes.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Check your current picture',
			},
			revision: 1,
		},
	],
};
